import { Chip, styled, useTheme } from '@mui/material';
import React from 'react';
import type { Theme } from '@mui/material';

interface StatusChipProps {
  status: string;
  children?: React.ReactNode;
  variant?: 'filled' | 'outlined';
}

const getStatusColors = (status: string, theme: Theme) => {
  const s = status.toLowerCase();
  if (s.includes('bad')) {
    return { 
      bg: theme.palette.mode === 'dark' ? '#3a2323' : '#fbeae5', 
      color: theme.palette.mode === 'dark' ? '#ff4d4f' : '#d9452b' 
    };
  } else if (s.includes('active')) {
    return { 
      bg: theme.palette.mode === 'dark' ? '#173F2C' : '#e6f4ea', 
      color: theme.palette.mode === 'dark' ? '#3DF06A' : '#1e8e3e' 
    };
  } else if (s.includes('closed')) {
    return { 
      bg: theme.palette.mode === 'dark' ? '#222' : '#f4f6f9', 
      color: theme.palette.mode === 'dark' ? '#888' : '#5a6470' 
    };
  } else if (s === 'paid') {
    return { 
      bg: theme.palette.mode === 'dark' ? '#173F2C' : '#e6f7ed', 
      color: theme.palette.mode === 'dark' ? '#3DF06A' : '#1e8e3e' 
    };
  } else if (s === 'partially paid') {
    return { 
      bg: theme.palette.mode === 'dark' ? '#2C1F3D' : '#f3e8ff', 
      color: theme.palette.mode === 'dark' ? '#98773b' : '#7c3aed' 
    };
  } else if (s === 'pending') {
    return { 
      bg: theme.palette.mode === 'dark' ? '#3D1F1F' : '#fee2e2', 
      color: theme.palette.mode === 'dark' ? '#FF4E4E' : '#dc2626' 
    };
  }
  return { 
    bg: theme.palette.mode === 'dark' ? '#1F2C3D' : '#f1f5f9', 
    color: theme.palette.mode === 'dark' ? '#4E9FFF' : '#475569' 
  };
};

const StyledChip = styled(Chip)<{ $bg: string; $color: string }>(({ $bg, $color }) => ({
  backgroundColor: $bg,
  color: $color,
  fontWeight: 500,
  borderRadius: 12,
  fontSize: '0.95em',
  textTransform: 'none',
  padding: '0 8px',
  height: 28,
}));

export const StatusChip: React.FC<StatusChipProps> = ({ status, children, variant = 'filled' }) => {
  const theme = useTheme();
  const { bg, color } = getStatusColors(status, theme);
  return (
    <StyledChip
      $bg={variant === 'filled' ? bg : 'transparent'}
      $color={color}
      label={children || status}
      size="small"
      variant={variant}
      sx={variant === 'outlined' ? { border: `1.5px solid ${color}` } : {}}
    />
  );
};

export default StatusChip;
